import { Currency } from '../locale';

export type HubSpotContactProperties = {
  accountcreatedat: string;
  account_type?: string;
  addons: string;
  currency: Currency;
  email: string;
  firstname?: string;
  has_platform_access: 'true' | 'false';
  ispaused: 'true' | 'false';
  lastname?: string;
  last_impact_shop_purchase_date?: string;
  plan: string;
  awaitinginitialoptin?: 'true' | 'false';
  is_secondary_user: 'true' | 'false';
  profile_has_impact: 'true' | 'false';
  migrated_to_new_app_date?: string;
  is_shopify_user?: 'true' | 'false';
  funded_project_ids?: string;
  impact_categories_funded?: string;
  funding_sources?: string;
};

export type HubSpotCompanyProperties = {
  accountcreatedat?: string;
  user_id: string;
  primary_email: string;
  username?: string;
  account_type?: string;
  currency?: Currency;
  funded_project_ids?: string;
  impact_categories_funded?: string;
  funding_sources?: string;
  last_purchase_date_shopify?: string;
  last_purchase_date_api?: string;
  last_purchase_date_subscriptions?: string;
  last_purchase_date_impact_shop?: string;
  last_purchase_date_assisted?: string;
  amount_spent_2y_shopify?: string;
  amount_spent_2y_api?: string;
  amount_spent_2y_subscriptions?: string;
  amount_spent_2y_impact_shop?: string;
  amount_spent_2y_assisted?: string;
  parent_profile_username?: string;
  child_profile_usernames?: string;
  sales_agent_name?: string;
  subscription_plan?: string;
  subscription_is_paused?: 'true' | 'false';
  last_impact_shop_purchase_date?: string;
  has_impact?: 'true' | 'false';
};

export type ClearedHubSpotCompanyProperties = {
  [K in keyof Required<HubSpotCompanyProperties>]: '';
};

export type HubSpotInvoiceProperties = {
  hs_number: string;
  hs_amount_billed: string;
  hs_currency: string;
  hs_invoice_status: 'draft' | 'open' | 'paid' | 'voided';
  hs_payment_date?: string;
  hs_invoice_date?: string;
  ecologi_invoice_id: string;
  ecologi_user_id: string;
  ecologi_order_variant?: string;
};

/**
 * Fails to compile unless `T` is `never`, naming the offending members in the error.
 *
 * The property-name lists below are what a reconciling sync reads back from HubSpot in order to
 * diff against. `keyof` alone only proves each name is valid, not that every key is listed — and a
 * property that is written but never read back differs on every comparison, so the record can never
 * settle. These guards turn that into a build failure.
 */
type AssertNever<T extends never> = T;

/** Set within the Nest API in api-v2; a reconciling sync must never overwrite them. */
type ContactPropertiesNotDerived = 'awaitinginitialoptin' | 'is_shopify_user';

export const hubSpotContactPropertyNames = [
  'accountcreatedat',
  'account_type',
  'addons',
  'currency',
  'email',
  'firstname',
  'has_platform_access',
  'lastname',
  'ispaused',
  'plan',
  'last_impact_shop_purchase_date',
  'is_secondary_user',
  'profile_has_impact',
  'migrated_to_new_app_date',
  'funded_project_ids',
  'impact_categories_funded',
  'funding_sources',
] as const satisfies readonly (keyof HubSpotContactProperties)[];

export type UnlistedContactProperties = AssertNever<
  Exclude<keyof HubSpotContactProperties, (typeof hubSpotContactPropertyNames)[number] | ContactPropertiesNotDerived>
>;

export const hubSpotCompanyPropertyNames = [
  'accountcreatedat',
  'user_id',
  'primary_email',
  'username',
  'account_type',
  'currency',
  'funded_project_ids',
  'impact_categories_funded',
  'funding_sources',
  'last_purchase_date_shopify',
  'last_purchase_date_api',
  'last_purchase_date_subscriptions',
  'last_purchase_date_impact_shop',
  'last_purchase_date_assisted',
  'amount_spent_2y_shopify',
  'amount_spent_2y_api',
  'amount_spent_2y_subscriptions',
  'amount_spent_2y_impact_shop',
  'amount_spent_2y_assisted',
  'parent_profile_username',
  'child_profile_usernames',
  'sales_agent_name',
  'subscription_plan',
  'subscription_is_paused',
  'last_impact_shop_purchase_date',
  'has_impact',
] as const satisfies readonly (keyof HubSpotCompanyProperties)[];

export type UnlistedCompanyProperties = AssertNever<
  Exclude<keyof HubSpotCompanyProperties, (typeof hubSpotCompanyPropertyNames)[number]>
>;

export const hubSpotInvoicePropertyNames = [
  'hs_number',
  'hs_amount_billed',
  'hs_currency',
  'hs_invoice_status',
  'hs_payment_date',
  'hs_invoice_date',
  'ecologi_invoice_id',
  'ecologi_user_id',
  'ecologi_order_variant',
] as const satisfies readonly (keyof HubSpotInvoiceProperties)[];

export type UnlistedInvoiceProperties = AssertNever<
  Exclude<keyof HubSpotInvoiceProperties, (typeof hubSpotInvoicePropertyNames)[number]>
>;
